import type { EfacturaStatus } from '@financiar/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Alert, Button, Card, Descriptions, Popconfirm, Space, Typography } from 'antd';
import { getApiError } from '../../api/client';
import type { InvoiceDetail } from '../../api/types';
import { triggerDownload } from '../../lib/download';
import {
  checkEfacturaStatus,
  efacturaDownloadUrl,
  efacturaPdfUrl,
  efacturaXmlUrl,
  markEfacturaSent,
  sendEfactura,
} from './api';
import { EfacturaTag } from './EfacturaTag';

const STATUS_LABELS: Record<EfacturaStatus, string> = {
  NOT_APPLICABLE: 'netrimisă',
  PENDING: 'în așteptare',
  UPLOADED: 'în prelucrare la ANAF',
  VALIDATED: 'validată de ANAF',
  REJECTED: 'respinsă de ANAF',
  ERROR: 'eroare',
};

/**
 * The e-Factura (ANAF) panel on the invoice detail — the single place that
 * shows the submission log (status, when it was sent, ANAF upload index, the
 * full list of rejection errors) and offers every ANAF action. Rendered only
 * for an issued, non-proforma invoice.
 */
export function EfacturaCard({ invoice }: { invoice: InvoiceDetail }) {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['invoices'] });
  const reportError = (error: unknown, fallback: string) => message.error(getApiError(error)?.message ?? fallback);

  const sendMutation = useMutation({
    mutationFn: sendEfactura,
    onSuccess: () => {
      message.success('Factura a fost trimisă la e-Factura (ANAF).');
      invalidate();
    },
    onError: (error) => reportError(error, 'Trimiterea la e-Factura a eșuat.'),
  });

  const checkMutation = useMutation({
    mutationFn: checkEfacturaStatus,
    onSuccess: (result) => {
      message.info(`Status e-Factura: ${STATUS_LABELS[result.efacturaStatus] ?? result.efacturaStatus}.`);
      invalidate();
    },
    onError: (error) => reportError(error, 'Verificarea stării e-Factura a eșuat.'),
  });

  const markMutation = useMutation({
    mutationFn: () => markEfacturaSent(invoice.id),
    onSuccess: () => {
      message.success('Factura a fost marcată ca trimisă în SPV.');
      invalidate();
    },
    onError: (error) => reportError(error, 'Marcarea a eșuat.'),
  });

  const status = invoice.efacturaStatus;
  const canSend = ['NOT_APPLICABLE', 'ERROR', 'REJECTED'].includes(status);
  const canCheck = ['PENDING', 'UPLOADED'].includes(status);
  const errors =
    (status === 'REJECTED' || status === 'ERROR') && invoice.efacturaMessage ? invoice.efacturaMessage.split('\n') : [];

  function confirmSend() {
    const isResend = status !== 'NOT_APPLICABLE';
    modal.confirm({
      title: isResend ? 'Retrimiți factura la e-Factura?' : 'Trimiți factura la e-Factura?',
      content: `Se generează XML-ul UBL și se încarcă la ANAF pentru ${invoice.fullNumber ?? 'acest document'}. Operațiunea nu poate fi anulată.`,
      okText: 'Trimite',
      cancelText: 'Renunță',
      onOk: () => sendMutation.mutateAsync(invoice.id),
    });
  }

  return (
    <Card title="e-Factura ANAF" extra={<EfacturaTag status={status} />} style={{ marginTop: 16 }}>
      {status === 'NOT_APPLICABLE' ? (
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Factura nu a fost trimisă încă la e-Factura.
        </Typography.Paragraph>
      ) : (
        <Descriptions column={{ xs: 1, md: 2 }} size="small" styles={{ label: { width: 150 } }}>
          <Descriptions.Item label="Stare">
            <EfacturaTag status={status} />
          </Descriptions.Item>
          {invoice.efacturaUploadedAt ? (
            <Descriptions.Item label="Trimisă la">
              {new Date(invoice.efacturaUploadedAt).toLocaleString('ro-RO')}
            </Descriptions.Item>
          ) : null}
          {invoice.efacturaUploadId ? (
            <Descriptions.Item label="Index încărcare ANAF">
              <span className="num">{invoice.efacturaUploadId}</span>
            </Descriptions.Item>
          ) : null}
          {invoice.efacturaDownloadId ? (
            <Descriptions.Item label="Index descărcare">
              <span className="num">{invoice.efacturaDownloadId}</span>
            </Descriptions.Item>
          ) : null}
        </Descriptions>
      )}

      {errors.length > 0 ? (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 8 }}
          message={status === 'REJECTED' ? 'Respinsă de ANAF — erori:' : 'Eroare la trimitere:'}
          description={
            <ul style={{ margin: 0, paddingInlineStart: 20 }}>
              {errors.map((err, index) => (
                <li key={index}>{err}</li>
              ))}
            </ul>
          }
        />
      ) : null}

      <Space wrap style={{ marginTop: 12 }}>
        {canSend ? (
          <Button type="primary" loading={sendMutation.isPending} onClick={confirmSend}>
            {status === 'NOT_APPLICABLE' ? 'Trimite la e-Factura' : 'Retrimite la e-Factura'}
          </Button>
        ) : null}
        {status === 'NOT_APPLICABLE' ? (
          <Popconfirm
            title="Marchezi factura ca deja trimisă în SPV?"
            description="Folosește doar dacă a fost trimisă din alt sistem. O marchează ca validată, fără a apela ANAF."
            okText="Marchează"
            cancelText="Renunță"
            onConfirm={() => markMutation.mutate()}
          >
            <Button loading={markMutation.isPending}>Marchează ca trimisă în SPV</Button>
          </Popconfirm>
        ) : null}
        {canCheck ? (
          <Button loading={checkMutation.isPending} onClick={() => checkMutation.mutate(invoice.id)}>
            Verifică status ANAF
          </Button>
        ) : null}
        {invoice.efacturaDownloadId ? (
          <Button onClick={() => triggerDownload(efacturaDownloadUrl(invoice.id))}>Descarcă răspuns semnat</Button>
        ) : null}
        <Button onClick={() => triggerDownload(efacturaPdfUrl(invoice.id))}>Descarcă PDF (ANAF)</Button>
        <Button onClick={() => triggerDownload(efacturaXmlUrl(invoice.id))}>Descarcă XML</Button>
      </Space>
    </Card>
  );
}
